import React, { useState } from 'react';
import { BomProposalCard, BomProposalCardProps, RobbieDockedPanel } from '../../components/robbie';
import { ProposalOutcome } from '../../hooks/useBomProposal';
import { BomEditProposal } from '../../types/bomProposal';
import { assessProposal, changeCount, CurrentBomLine } from '../../utils/bomProposal';
import Section from './Section';

/**
 * /design — Robbie's BOM proposal card in the docked panel at rail width:
 * one live stand-in (tick, Apply, Dismiss — no backend; Apply "succeeds"
 * after a beat) and every settled or refused state from sample data.
 */

const PROPOSAL: BomEditProposal = {
  bomId: 'BOM-AMR-1',
  summary: 'Three lines name their part in the description but not in the MPN column, and line 4 has no quantity I can trust.',
  edits: [
    {
      componentId: 'C-3', name: 'Motor driver, 3-phase gate, 100V', partNumber: null,
      changes: { partNumber: 'DRV-8353-RS' }, before: { partNumber: null },
      reason: 'Your other drivers follow the XXX-0000-XX scheme; this keeps the line searchable.',
    },
    {
      componentId: 'C-4', name: 'Power cable, AWG12, silicone', partNumber: null,
      changes: { mpn: '3132-12-RD', manufacturer: 'Alpha Wire', quantity: 8 },
      before: { mpn: null, manufacturer: null, quantity: 6 },
      reason: 'The description names Alpha Wire 3132; four motors need two runs each.',
    },
    {
      componentId: 'C-5', name: 'Deep-groove ball bearing, sealed', partNumber: 'BRG-6204-2RS',
      changes: { description: '6204-2RS deep-groove ball bearing, 20 × 47 × 14 mm, rubber sealed both sides' },
      before: { description: 'Sealed bearing for drive shaft, 20mm bore' },
      reason: 'Normalised to bore × OD × width so suppliers quote the same part.',
    },
  ],
};

const LINES: CurrentBomLine[] = [
  { componentId: 'C-3', name: 'Motor driver, 3-phase gate, 100V' },
  { componentId: 'C-4', name: 'Power cable, AWG12, silicone', quantity: 6 },
  { componentId: 'C-5', name: 'Deep-groove ball bearing, sealed', description: 'Sealed bearing for drive shaft, 20mm bore' },
];

const ROBBIE_TEXT = 'I propose 5 changes on 3 lines. Nothing on the BOM changes until you apply them.';

const fixed = (overrides: Partial<BomProposalCardProps>, lines: CurrentBomLine[] = LINES, bomId = 'BOM-AMR-1'): BomProposalCardProps => {
  const assessment = assessProposal(PROPOSAL, bomId, lines);
  const fresh = PROPOSAL.edits.filter((e) => assessment.lines[e.componentId] === 'fresh');
  return {
    proposal: PROPOSAL,
    outcome: { status: 'open' },
    assessment,
    isTicked: (id) => fresh.some((e) => e.componentId === id),
    selectedChanges: changeCount(fresh),
    applying: false,
    error: null,
    canApply: true,
    onToggle: () => undefined,
    onApply: () => undefined,
    onDismiss: () => undefined,
    ...overrides,
  };
};

/** Stand-in for useBomProposal: real ticking and outcomes, no network. */
const LiveCard: React.FC = () => {
  const [unticked, setUnticked] = useState<Set<string>>(new Set());
  const [outcome, setOutcome] = useState<ProposalOutcome>({ status: 'open' });
  const [applying, setApplying] = useState(false);
  const selected = PROPOSAL.edits.filter((e) => !unticked.has(e.componentId));
  return (
    <BomProposalCard
      {...fixed({})}
      outcome={outcome}
      isTicked={(id) => !unticked.has(id)}
      selectedChanges={changeCount(selected)}
      applying={applying}
      onToggle={(id) =>
        setUnticked((prev) => {
          const next = new Set(prev);
          if (next.has(id)) next.delete(id);
          else next.add(id);
          return next;
        })
      }
      onApply={() => {
        setApplying(true);
        setTimeout(() => {
          setApplying(false);
          setOutcome({ status: 'applied', count: changeCount(selected), version: 4 });
        }, 700);
      }}
      onDismiss={() => setOutcome({ status: 'dismissed' })}
    />
  );
};

const Panel: React.FC<{ label: string; card: React.ReactNode }> = ({ label, card }) => (
  <div className="flex flex-col gap-s2">
    <span className="text-meta text-ink-muted">{label}</span>
    <div className="h-[560px] w-robbie-rail overflow-hidden rounded-card border border-line">
      <RobbieDockedPanel
        className="w-full"
        messages={[
          { id: 'u', role: 'user', content: 'Review all 5 lines: fill missing part numbers, MPNs and quantities, flag duplicates, normalise descriptions, and tell me what you\'re unsure about.' },
          { id: 'r', content: <>{ROBBIE_TEXT}{card}</> },
        ]}
        input={{ value: '', onChange: () => undefined, onSend: () => undefined, placeholder: 'Ask Robbie…' }}
      />
    </div>
  </div>
);

const changedLines = LINES.map((l) => (l.componentId === 'C-4' ? { ...l, quantity: 10 } : l)).filter((l) => l.componentId !== 'C-5');

export const BomProposalGallery: React.FC = () => (
  <Section title="Robbie — BOM proposal card (Enhance with Robbie)">
    <div className="flex flex-wrap gap-s6">
      <Panel label="Live stand-in — tick, apply, dismiss" card={<LiveCard />} />
      <Panel label="Applied" card={<BomProposalCard {...fixed({ outcome: { status: 'applied', count: 5, version: 4 } })} />} />
      <Panel label="Nothing to apply (noChanges)" card={<BomProposalCard {...fixed({ outcome: { status: 'unchanged' } })} />} />
      <Panel label="Dismissed" card={<BomProposalCard {...fixed({ outcome: { status: 'dismissed' } })} />} />
      <Panel
        label="Apply refused — card stays"
        card={<BomProposalCard {...fixed({ error: 'I couldn\'t apply these, so the BOM is as it was: Component "C-4" is not on this BOM. Try again, or make the change in the BOM sheet.' })} />}
      />
      <Panel label="Lines changed / removed since" card={<BomProposalCard {...fixed({}, changedLines)} />} />
      <Panel label="BOM re-extracted since" card={<BomProposalCard {...fixed({}, LINES, 'BOM-AMR-2')} />} />
      <Panel label="Reading along (viewer)" card={<BomProposalCard {...fixed({ canApply: false })} />} />
    </div>
  </Section>
);

export default BomProposalGallery;
